import {
  CurrencyCode,
  FinanceActivityStatus,
  FinanceCategoryScope,
  type FinanceExpenseCategorySummary,
  type FinanceIncomeCategorySummary,
  type FinanceTransactionMemberSummary,
  type FinanceTransactionRecord,
} from "@/features/finance/model/finance-types"
import {
  isMoneyString,
} from "@ibada/shared-types/finance"

export function normalizeString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}


export function normalizeNullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null
  return normalizeString(value) ?? null
}


/**
 * For values that are genuinely NUMBERS — counts, page/limit/total, basis points, ratios. Never for money.
 *
 * Money has its own normalizer below, and the distinction is the whole point: `Number("9007199254740993.01")`
 * silently loses the cent, and a count cannot lose a cent because it has none.
 */
export function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}


/**
 * Money arrives from the API as a canonical decimal string at the currency's scale (IBA-116) — `"1500.00"` in
 * USD, `"1500"` in RWF — and it stays one all the way to `formatCurrency`, which parses a decimal string
 * EXACTLY. It is deliberately NOT put through `Number()`: that is the precision the wire format exists to
 * preserve, and a float is where it used to be thrown away.
 *
 * A `number` is still accepted, because an un-migrated endpoint may send one; it is converted through its
 * decimal representation, never by arithmetic.
 */
export function normalizeMoney(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return isMoneyString(trimmed) ? trimmed : undefined
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const text = String(value)
    return isMoneyString(text) ? text : undefined
  }
  return undefined
}


export function normalizeNullableMoney(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null
  return normalizeMoney(value) ?? null
}


/**
 * The money fallback when a field is missing. `"0"` is a valid money string in every currency, and `Intl`
 * renders it at the currency's own scale (`$0.00`, `0 RF`) — so no currency is needed to spell zero here.
 */
export const ZERO_MONEY = "0"


export function normalizeBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    return normalized === "true" || normalized === "1" || normalized === "yes"
  }
  return false
}


export function normalizeCurrencyCode(value: unknown): CurrencyCode | undefined {
  if (value === CurrencyCode.CDF) return CurrencyCode.CDF
  if (value === CurrencyCode.EUR) return CurrencyCode.EUR
  if (value === CurrencyCode.RWF) return CurrencyCode.RWF
  if (value === CurrencyCode.USD) return CurrencyCode.USD
  return undefined
}


export function normalizeActivityStatus(value: unknown): FinanceActivityStatus {
  if (value === FinanceActivityStatus.Completed) return FinanceActivityStatus.Completed
  if (value === FinanceActivityStatus.Cancelled) return FinanceActivityStatus.Cancelled
  return FinanceActivityStatus.Active
}


export function normalizeTransactionAccountSummary(
  value: unknown,
): FinanceTransactionRecord["account"] {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const branchId = normalizeString(record.branchId ?? record.branch_id)
  const name = normalizeString(record.name)
  const status = normalizeString(record.status)

  if (!id || !branchId || !name) return null

  return {
    id,
    branchId,
    name,
    status: status === "ARCHIVED" ? "ARCHIVED" : "ACTIVE",
  }
}



export function extractFilename(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) return null
  const utfMatch = /filename\\*=UTF-8''([^;]+)/i.exec(contentDisposition)
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1])
    } catch {
      return utfMatch[1]
    }
  }
  const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition)
  if (asciiMatch?.[1]) return asciiMatch[1]
  return null
}


export function normalizeFinanceCategoryScope(value: unknown): FinanceCategoryScope | undefined {
  if (value === "GLOBAL" || value === FinanceCategoryScope.Shared) return FinanceCategoryScope.Shared
  if (value === FinanceCategoryScope.Branch || value === FinanceCategoryScope.Parish) return value
  return undefined
}


export function deriveCategoryScope(
  branchId: string | null,
  branchType: string | null | undefined,
): FinanceCategoryScope {
  if (!branchId) return FinanceCategoryScope.Shared
  return branchType === "SYSTEM" ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch
}


export function normalizeBranchSummary(value: unknown): FinanceTransactionRecord["branch"] {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  const slug = normalizeString(record.slug)
  const status = normalizeString(record.status)

  if (!id || !name || !slug || !status) return null

  return { id, name, slug, status }
}



export function normalizeNamedEntity(value: unknown): { id: string; name: string } | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  if (!id || !name) return null
  return { id, name }
}


export function extractListPayload<T>(
  input: unknown,
  itemKeys: string[],
  normalize: (value: unknown) => T
): T[] {
  if (Array.isArray(input)) {
    return input.map(normalize)
  }

  if (!input || typeof input !== "object") {
    return []
  }

  const record = input as Record<string, unknown>
  for (const key of itemKeys) {
    if (Array.isArray(record[key])) {
      return record[key].map(normalize)
    }
  }

  if (record.data && typeof record.data === "object") {
    return extractListPayload(record.data, itemKeys, normalize)
  }

  return []
}


export function extractPaginatedListPayload<T>(
  input: unknown,
  itemKeys: string[],
  normalize: (value: unknown) => T,
): { items: T[]; total: number; page: number; limit: number } {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { items: [], total: 0, page: 1, limit: 20 }
  }

  const record = input as Record<string, unknown>
  const data =
    record.data && typeof record.data === "object" && !Array.isArray(record.data)
      ? (record.data as Record<string, unknown>)
      : record

  const items = extractListPayload(data, itemKeys, normalize)
  const total = normalizeNumber(data.total) ?? items.length
  const page = normalizeNumber(data.page) ?? 1
  const limit = normalizeNumber(data.limit) ?? Math.max(items.length, 1)

  return {
    items,
    total,
    page,
    limit,
  }
}

export type FinanceTransactionListResponse = {
  items: FinanceTransactionRecord[]
  total: number
  page: number
  limit: number
}

export function normalizeMemberSummary(value: unknown): FinanceTransactionMemberSummary | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>

  const id = normalizeString(record.id)
  const firstName = normalizeString(record.firstName ?? record.first_name)
  const lastName = normalizeString(record.lastName ?? record.last_name)

  if (!id || !firstName || !lastName) {
    return null
  }

  return {
    id,
    firstName,
    middleName: normalizeNullableString(record.middleName ?? record.middle_name),
    lastName,
  }
}

export function normalizeIncomeCategorySummary(value: unknown): FinanceIncomeCategorySummary | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  if (!id || !name) return null

  const branchId = normalizeNullableString(record.branchId ?? record.branch_id)
  const branchType =
    record.branch && typeof record.branch === "object"
      ? normalizeString((record.branch as Record<string, unknown>).branchType ?? (record.branch as Record<string, unknown>).branch_type)
      : null
  const scope = normalizeFinanceCategoryScope(record.scope) ?? deriveCategoryScope(branchId, branchType)

  return {
    id,
    name,
    branchId,
    isParishOnly: scope === FinanceCategoryScope.Parish,
    scope,
  }
}

export function normalizeExpenseCategorySummary(value: unknown): FinanceExpenseCategorySummary | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  if (!id || !name) return null
  const branchId = normalizeNullableString(record.branchId ?? record.branch_id)
  const branchType =
    record.branch && typeof record.branch === "object"
      ? normalizeString((record.branch as Record<string, unknown>).branchType ?? (record.branch as Record<string, unknown>).branch_type)
      : null
  return {
    id,
    name,
    branchId,
    scope: normalizeFinanceCategoryScope(record.scope) ?? deriveCategoryScope(branchId, branchType),
  }
}
